/**
 * A.6 — save schema v1 persistence for settings.
 *
 * Key: `lumenabyss.settings.v1`. Envelope `{ save_version, written_at, checksum, data }`
 * where `checksum` is fnv1a32 over the canonical JSON of `data` only, so a re-save of
 * unchanged settings round-trips bitwise regardless of when it was written.
 *
 * Corruption (parse failure or checksum mismatch) deletes only this key and regenerates
 * defaults; `meta.v1` and `run.v1` are never touched. The game always boots.
 */

import { fnv1a32 } from '../core/rng';
import type { BindableAction, Settings } from './types';

export const SETTINGS_KEY = 'lumenabyss.settings.v1';
export const SAVE_VERSION = 1;

/** §3.1 default binding table. `alt` of `UNBOUND` is the §3.9 flag value. */
export const UNBOUND = 'UNBOUND';

export const BINDABLE_ACTIONS: readonly BindableAction[] = [
  { id: 'move_up', label: 'Move Up', primary: 'KeyW', alt: 'ArrowUp' },
  { id: 'move_down', label: 'Move Down', primary: 'KeyS', alt: 'ArrowDown' },
  { id: 'move_left', label: 'Move Left', primary: 'KeyA', alt: 'ArrowLeft' },
  { id: 'move_right', label: 'Move Right', primary: 'KeyD', alt: 'ArrowRight' },
  { id: 'focus_aim', label: 'Focus Aim (hold)', primary: 'Mouse2', alt: UNBOUND },
  { id: 'dash', label: 'Dash', primary: 'Space', alt: 'ShiftLeft' },
  { id: 'pause', label: 'Pause', primary: 'Escape', alt: 'Gamepad9', reserved: true },
  { id: 'select_1', label: 'Level-Up Select 1', primary: 'Digit1', alt: 'Mouse0' },
  { id: 'select_2', label: 'Level-Up Select 2', primary: 'Digit2', alt: UNBOUND },
  { id: 'select_3', label: 'Level-Up Select 3', primary: 'Digit3', alt: UNBOUND },
  { id: 'reroll', label: 'Level-Up Reroll', primary: 'KeyR', alt: 'Gamepad2' },
  { id: 'ui_confirm', label: 'UI Confirm', primary: 'Enter', alt: 'Mouse0' },
  { id: 'ui_back', label: 'UI Back', primary: 'Escape', alt: 'Gamepad1' },
];

/** §13.1.2 targeting policies (§5.3 table, minus the Focus Aim overlay). */
export const TARGETING_POLICIES: readonly { id: string; label: string; blurb: string }[] = [
  { id: 'nearest', label: 'Nearest', blurb: 'Shoots the closest enemy.' },
  { id: 'nearest-threat', label: 'Nearest Threat', blurb: 'Prefers dangerous enemies that are also close.' },
  { id: 'lowest-hp', label: 'Lowest HP', blurb: 'Finishes off the most wounded enemy nearby.' },
  { id: 'highest-hp', label: 'Highest HP', blurb: 'Chews on the toughest enemy nearby.' },
  { id: 'densest-cluster', label: 'Densest Cluster', blurb: 'Fires into the thickest part of the swarm.' },
  { id: 'forward-cone', label: 'Forward Cone', blurb: 'Shoots ahead of the way you are moving.' },
  { id: 'boss-priority', label: 'Boss Priority', blurb: 'Always picks the boss when one is in range.' },
  { id: 'elite-priority', label: 'Elite Priority', blurb: 'Always picks an elite when one is in range.' },
];

/** A.6 defaults = spine §14 minimum bar + §3.5 dead zones + §3.1 binding table. */
export function defaultSettings(): Settings {
  const bindings: Record<string, { primary: string; alt: string }> = {};
  for (let i = 0; i < BINDABLE_ACTIONS.length; i++) {
    const a = BINDABLE_ACTIONS[i];
    bindings[a.id] = { primary: a.primary, alt: a.alt };
  }
  return {
    save_version: SAVE_VERSION,
    input: { bindings, gamepad_deadzone: { inner: 0.18, outer: 0.92 } },
    video: {
      bloom_intensity: 1.0,
      colorblind_mode: 'off',
      reduced_flashes: false,
      reduced_particles: false,
      reduced_shake: false,
      text_scale: 1.0,
      window_mode: 'windowed',
      damage_numbers: true,
      scanline_intensity: 0.18,
      vhs_intensity: 0.22,
      perf_tier: 'auto',
    },
    audio: { master: 0.8, music: 0.7, sfx: 0.8 },
    access: { pause_on_focus_loss: true },
    gameplay: { onboarding_seen: false },
    targeting_policy_default: 'nearest',
  };
}

/** Stable canonical JSON — keys sorted, no whitespace. Matches `core/content.ts`. */
export function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) {
    let out = '[';
    for (let i = 0; i < value.length; i++) {
      if (i > 0) out += ',';
      out += canonicalJson(value[i]);
    }
    return out + ']';
  }
  const rec = value as Record<string, unknown>;
  const keys = Object.keys(rec).sort();
  let out = '{';
  for (let i = 0; i < keys.length; i++) {
    if (i > 0) out += ',';
    out += JSON.stringify(keys[i]) + ':' + canonicalJson(rec[keys[i]]);
  }
  return out + '}';
}

export function checksumOf(data: unknown): string {
  return fnv1a32(canonicalJson(data)).toString(16).padStart(8, '0');
}

/**
 * A.6 migration registry. `migrations[n]` upgrades a v`n` payload to v`n+1`.
 * A payload with a `save_version` above `SAVE_VERSION` is discarded, never crashed on.
 */
export type Migration = (data: Record<string, unknown>) => Record<string, unknown>;
export const SETTINGS_MIGRATIONS: Record<number, Migration> = {};

export type LoadStatus = 'loaded' | 'missing' | 'corrupt' | 'future-version' | 'migrated';

export interface LoadResult {
  settings: Settings;
  status: LoadStatus;
}

/** Minimal `Storage` surface so tests can inject a fake without a DOM. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function safeStorage(): StorageLike | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const probe = '__la_probe__';
    localStorage.setItem(probe, '1');
    localStorage.removeItem(probe);
    return localStorage;
  } catch {
    return null;
  }
}

/** Deep merge of a loaded payload over defaults — unknown keys are dropped, missing keys filled. */
function coerce(raw: unknown): Settings {
  const out = defaultSettings();
  if (raw === null || typeof raw !== 'object') return out;
  const r = raw as Record<string, unknown>;

  const input = r.input as Record<string, unknown> | undefined;
  if (input && typeof input === 'object') {
    const b = input.bindings as Record<string, unknown> | undefined;
    if (b && typeof b === 'object') {
      for (const key of Object.keys(out.input.bindings)) {
        const slot = b[key] as Record<string, unknown> | undefined;
        if (slot && typeof slot === 'object') {
          if (typeof slot.primary === 'string') out.input.bindings[key].primary = slot.primary;
          if (typeof slot.alt === 'string') out.input.bindings[key].alt = slot.alt;
        }
      }
    }
    const dz = input.gamepad_deadzone as Record<string, unknown> | undefined;
    if (dz && typeof dz === 'object') {
      if (typeof dz.inner === 'number') out.input.gamepad_deadzone.inner = clamp(dz.inner, 0.1, 0.3);
      if (typeof dz.outer === 'number') out.input.gamepad_deadzone.outer = clamp(dz.outer, 0.8, 1.0);
    }
  }

  const video = r.video as Record<string, unknown> | undefined;
  if (video && typeof video === 'object') {
    if (typeof video.bloom_intensity === 'number') out.video.bloom_intensity = clamp(video.bloom_intensity, 0, 1);
    if (isColorblind(video.colorblind_mode)) out.video.colorblind_mode = video.colorblind_mode;
    if (typeof video.reduced_flashes === 'boolean') out.video.reduced_flashes = video.reduced_flashes;
    if (typeof video.reduced_particles === 'boolean') out.video.reduced_particles = video.reduced_particles;
    if (typeof video.reduced_shake === 'boolean') out.video.reduced_shake = video.reduced_shake;
    if (typeof video.text_scale === 'number') out.video.text_scale = nearestTextScale(video.text_scale);
    if (video.window_mode === 'windowed' || video.window_mode === 'fullscreen') out.video.window_mode = video.window_mode;
    if (typeof video.damage_numbers === 'boolean') out.video.damage_numbers = video.damage_numbers;
    if (typeof video.scanline_intensity === 'number') out.video.scanline_intensity = clamp(video.scanline_intensity, 0, 0.35);
    if (typeof video.vhs_intensity === 'number') out.video.vhs_intensity = clamp(video.vhs_intensity, 0, 1);
    if (
      video.perf_tier === 'auto' ||
      video.perf_tier === 'desktop' ||
      video.perf_tier === 'standard' ||
      video.perf_tier === 'handheld'
    ) {
      out.video.perf_tier = video.perf_tier;
    }
  }

  const audio = r.audio as Record<string, unknown> | undefined;
  if (audio && typeof audio === 'object') {
    if (typeof audio.master === 'number') out.audio.master = clamp(audio.master, 0, 1);
    if (typeof audio.music === 'number') out.audio.music = clamp(audio.music, 0, 1);
    if (typeof audio.sfx === 'number') out.audio.sfx = clamp(audio.sfx, 0, 1);
  }

  const access = r.access as Record<string, unknown> | undefined;
  if (access && typeof access === 'object' && typeof access.pause_on_focus_loss === 'boolean') {
    out.access.pause_on_focus_loss = access.pause_on_focus_loss;
  }

  const gameplay = r.gameplay as Record<string, unknown> | undefined;
  if (gameplay && typeof gameplay === 'object' && typeof gameplay.onboarding_seen === 'boolean') {
    out.gameplay.onboarding_seen = gameplay.onboarding_seen;
  }

  if (typeof r.targeting_policy_default === 'string') {
    for (let i = 0; i < TARGETING_POLICIES.length; i++) {
      if (TARGETING_POLICIES[i].id === r.targeting_policy_default) {
        out.targeting_policy_default = r.targeting_policy_default;
        break;
      }
    }
  }
  return out;
}

function clamp(v: number, lo: number, hi: number): number {
  if (!Number.isFinite(v)) return lo;
  return v < lo ? lo : v > hi ? hi : v;
}

export const TEXT_SCALES: readonly number[] = [1.0, 1.15, 1.3, 1.5];

function nearestTextScale(v: number): number {
  let best = TEXT_SCALES[0];
  let bestD = Infinity;
  for (let i = 0; i < TEXT_SCALES.length; i++) {
    const d = Math.abs(TEXT_SCALES[i] - v);
    if (d < bestD) {
      bestD = d;
      best = TEXT_SCALES[i];
    }
  }
  return best;
}

function isColorblind(v: unknown): v is Settings['video']['colorblind_mode'] {
  return v === 'off' || v === 'deuteranopia' || v === 'protanopia' || v === 'tritanopia';
}

export class SettingsStore {
  private storage: StorageLike | null;
  private current: Settings;
  private lastStatus: LoadStatus = 'missing';

  constructor(storage?: StorageLike | null) {
    this.storage = storage === undefined ? safeStorage() : storage;
    const r = this.load();
    this.current = r.settings;
    this.lastStatus = r.status;
  }

  get value(): Settings {
    return this.current;
  }

  get status(): LoadStatus {
    return this.lastStatus;
  }

  /** A.6 resume rules: verify checksum → verify save_version → migrate → coerce. */
  load(): LoadResult {
    const store = this.storage;
    if (!store) return { settings: defaultSettings(), status: 'missing' };
    let text: string | null = null;
    try {
      text = store.getItem(SETTINGS_KEY);
    } catch {
      return { settings: defaultSettings(), status: 'corrupt' };
    }
    if (text === null) return { settings: defaultSettings(), status: 'missing' };

    let envelope: unknown;
    try {
      envelope = JSON.parse(text);
    } catch {
      this.discard();
      return { settings: defaultSettings(), status: 'corrupt' };
    }
    if (envelope === null || typeof envelope !== 'object') {
      this.discard();
      return { settings: defaultSettings(), status: 'corrupt' };
    }
    const env = envelope as Record<string, unknown>;
    const version = typeof env.save_version === 'number' ? env.save_version : -1;
    if (version < 1) {
      this.discard();
      return { settings: defaultSettings(), status: 'corrupt' };
    }
    if (version > SAVE_VERSION) {
      this.discard();
      return { settings: defaultSettings(), status: 'future-version' };
    }
    const data = env.data;
    if (data === null || typeof data !== 'object') {
      this.discard();
      return { settings: defaultSettings(), status: 'corrupt' };
    }
    if (typeof env.checksum !== 'string' || env.checksum !== checksumOf(data)) {
      this.discard();
      return { settings: defaultSettings(), status: 'corrupt' };
    }

    let payload = data as Record<string, unknown>;
    let migrated = false;
    for (let v = version; v < SAVE_VERSION; v++) {
      const fn = SETTINGS_MIGRATIONS[v];
      if (!fn) {
        this.discard();
        return { settings: defaultSettings(), status: 'corrupt' };
      }
      payload = fn(payload);
      migrated = true;
    }
    return { settings: coerce(payload), status: migrated ? 'migrated' : 'loaded' };
  }

  /** Writes immediately (§13.1.3: changes apply live, persist immediately). */
  save(next?: Settings): void {
    if (next) this.current = next;
    const store = this.storage;
    if (!store) return;
    const data = this.current;
    const envelope = {
      save_version: SAVE_VERSION,
      written_at: new Date().toISOString(),
      checksum: checksumOf(data),
      data,
    };
    try {
      store.setItem(SETTINGS_KEY, JSON.stringify(envelope));
    } catch {
      // Quota or private-mode failure: settings stay live for this session.
    }
  }

  /** Mutate through a callback, then persist. Returns the live settings object. */
  update(mutate: (s: Settings) => void): Settings {
    mutate(this.current);
    this.save();
    return this.current;
  }

  /** §3.9 restore defaults — reproduces the A.6 default block bitwise. */
  restoreDefaults(): Settings {
    this.current = defaultSettings();
    this.save();
    return this.current;
  }

  /** Restores only the §3.1 binding table, leaving other settings untouched. */
  restoreBindings(): Settings {
    const fresh = defaultSettings();
    this.current.input.bindings = fresh.input.bindings;
    this.current.input.gamepad_deadzone = fresh.input.gamepad_deadzone;
    this.save();
    return this.current;
  }

  /**
   * §3.9 steal semantics: assigning an in-use code within the same device class
   * unbinds the previous owner's slot, leaving exactly one owner.
   */
  bind(actionId: string, slot: 'primary' | 'alt', code: string): Settings {
    const bindings = this.current.input.bindings;
    const target = bindings[actionId];
    if (!target) return this.current;
    if (code !== UNBOUND) {
      const cls = deviceClass(code);
      for (const key of Object.keys(bindings)) {
        const b = bindings[key];
        if (key === actionId && b === target) {
          if (slot === 'primary' && b.alt === code) b.alt = UNBOUND;
          if (slot === 'alt' && b.primary === code) b.primary = UNBOUND;
          continue;
        }
        if (b.primary === code && deviceClass(b.primary) === cls) b.primary = UNBOUND;
        if (b.alt === code && deviceClass(b.alt) === cls) b.alt = UNBOUND;
      }
    }
    target[slot] = code;
    this.save();
    return this.current;
  }

  /** A.6 corruption fallback: delete only this key. */
  private discard(): void {
    try {
      this.storage?.removeItem(SETTINGS_KEY);
    } catch {
      // ignore
    }
  }
}

export type DeviceClass = 'keyboard' | 'mouse' | 'gamepad' | 'none';

/** §3.9 — device classes conflict only within their class. */
export function deviceClass(code: string): DeviceClass {
  if (code === UNBOUND || code === '') return 'none';
  if (code.startsWith('Mouse')) return 'mouse';
  if (code.startsWith('Gamepad')) return 'gamepad';
  return 'keyboard';
}

/** Human-readable code for the remap table. */
export function codeLabel(code: string): string {
  if (code === UNBOUND || code === '') return 'Unbound';
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  if (code === 'Mouse0') return 'Left Mouse';
  if (code === 'Mouse1') return 'Middle Mouse';
  if (code === 'Mouse2') return 'Right Mouse';
  if (code.startsWith('Gamepad')) return 'Pad ' + code.slice(7);
  if (code.startsWith('Arrow')) return code.slice(5) + ' Arrow';
  if (code === 'ShiftLeft') return 'Left Shift';
  if (code === 'ShiftRight') return 'Right Shift';
  return code;
}
